import { iswindow } from "@client/entry";
import { SCRAMJETCLIENT } from "@/symbols";
import { ScramjetClient } from "@client/index";
import {
  Object_defineProperty,
  Object_getOwnPropertyDescriptor,
  Reflect_apply,
} from "@/shared/snapshot";
import { POLLUTANT } from "./realm";

export default function (client: ScramjetClient, self: Self) {
  if (iswindow)
    client.Proxy("window.postMessage", {
      apply(ctx) {
        // This hook runs in the receiving window's realm. The caller helper
        // supplies an object from the sending realm so primitive messages
        // can carry the sender's logical origin as well.

        let pollutant;

        if (
          ctx.this &&
          POLLUTANT in ctx.this &&
          typeof ctx.this[POLLUTANT] === "object" &&
          ctx.this[POLLUTANT] !== null
        ) {
          // The caller helper supplies this explicitly, even for primitive
          // messages and payload objects borrowed from a different realm.
          pollutant = ctx.this[POLLUTANT];
        } else if (typeof ctx.args[0] === "object" && ctx.args[0] !== null) {
          pollutant = ctx.args[0];
        } else if (typeof ctx.args[2] === "object" && ctx.args[2] !== null) {
          pollutant = ctx.args[2];
        } else {
          pollutant = {}; // give up
        }

        // and now we can steal Function from the caller's realm
        const {
          constructor: { constructor: Function },
        } = pollutant;

        // invoking stolen function will give us the caller's globalThis, remember scramjet has already proxied it!!!
        const callerGlobalThisProxied: Self = Function("return globalThis")();
        const callerClient = callerGlobalThisProxied[SCRAMJETCLIENT];

        // this WOULD be enough but the source argument of MessageEvent has to return the caller's window
        // and if we just call it normally it would be coming from here, which WILL NOT BE THE CALLER'S because the accessor is from the parent
        // so with the stolen function we wrap postmessage so the source will truly be the caller's window (remember that function is scramjet's!!!)
        const wrappedPostMessage = Function("...args", "this(...args)");
        if (!callerClient) {
          // Native SDKs in opaque frames have no proxy client. Keep their
          // data, targetOrigin, and transfers intact for the browser.
          ctx.return(wrappedPostMessage.call(ctx.fn, ...ctx.args));
          return;
        }

        // console.log(
        // 	callerClient,
        // 	client,
        // 	callerGlobalThisProxied.document,
        // 	self.document,
        // 	callerClient === client
        // );
        const inherit =
          callerClient.url.href === "about:srcdoc" ||
          callerClient.url.href === "about:blank";
        ctx.args[0] = {
          $scramjet$messagetype: "window",
          $scramjet$origin: inherit
            ? callerClient.global.parent[SCRAMJETCLIENT].url.origin
            : callerClient.url.origin,
          $scramjet$data: ctx.args[0],
        };
        // console.error("?", ctx.args);
        // eval("debugger");

        // * origin because obviously
        if (typeof ctx.args[1] === "string") ctx.args[1] = "*";
        if (typeof ctx.args[1] === "object") ctx.args[1].targetOrigin = "*";

        ctx.return(wrappedPostMessage.call(ctx.fn, ...ctx.args));
      },
    });

  client.Proxy("BroadcastChannel.prototype.postMessage", {
    apply(ctx) {
      ctx.args[0] = {
        $scramjet$messagetype: "window",
        // TODO: need to actually look up the broadcastchannel itself in box i think
        $scramjet$origin: client.url.origin,
        $scramjet$data: ctx.args[0],
      };
    },
  });

  const toproxy = ["MessagePort.prototype.postMessage"];

  if (self.Worker) toproxy.push("Worker.prototype.postMessage");
  if (!iswindow) toproxy.push("self.postMessage"); // only do the generic version if we're in a worker

  client.Proxy(toproxy, {
    apply(ctx) {
      // origin/source doesn't need to be preserved - it's null in the message event

      ctx.args[0] = {
        $scramjet$messagetype: "worker",
        $scramjet$data: ctx.args[0],
      };
    },
  });
  Object_defineProperty(self, client.config.globals.wrappostmessagefn, {
    value: function (obj: any) {
      if (!obj) return obj;
      const method = obj.postMessage;
      if (typeof method !== "function") return obj;
      return {
        postMessage: new Proxy(method.bind(obj), {
          apply(target, that, args) {
            let previous: PropertyDescriptor | undefined;
            let marked = false;
            try {
              if (obj[SCRAMJETCLIENT]) {
                previous = Object_getOwnPropertyDescriptor(obj, POLLUTANT);
                Object_defineProperty(obj, POLLUTANT, {
                  value: {},
                  configurable: true,
                });
                marked = true;
              }
            } catch {
              // Native cross-origin windows cannot be inspected or marked.
            }
            try {
              return Reflect_apply(target, that, args);
            } finally {
              if (marked) {
                if (previous) Object_defineProperty(obj, POLLUTANT, previous);
                else delete obj[POLLUTANT];
              }
            }
          },
        }),
      };
    },
    configurable: false,
    writable: false,
    enumerable: false,
  });
}
